import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BlogCategory, BlogPost } from '../../types/Blog';
import { loadLiveBlogData, normalizeBlogPost } from '../../utils/BlogAPI';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import {
  categoriesInUse,
  formatStoryDate,
  homepageStories,
  imageSource,
  primaryCategory,
  publishedPosts,
} from '../../utils/blogPresentation';
import { homeStoryDelivery, journalRowDelivery, photographAlt } from '../../utils/imageSeo';
import './BlogSection.css';

function storyPicture(url: string, count: number, source?: { width?: number; height?: number } | null) {
  const delivery = count > 0 ? homeStoryDelivery(url, count, source) : journalRowDelivery(url, source);
  return {
    src: delivery.src,
    srcSet: delivery.srcSet || undefined,
    sizes: delivery.srcSet ? delivery.sizes : undefined,
    width: delivery.width,
    height: delivery.height,
  };
}

interface BlogSectionProps {
  variant?: 'home' | 'index';
}

const BlogSection: React.FC<BlogSectionProps> = ({ variant = 'index' }) => {
  const displaySettings = useDisplaySettings();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    let active = true;

    const loadBlogData = async () => {
      try {
        const { posts: serverPosts, categories: serverCategories } = await loadLiveBlogData();
        if (!active) return;
        const normalizedPosts = serverPosts.map((post) => normalizeBlogPost(post));
        setPosts(normalizedPosts);
        setCategories(serverCategories);
        if (normalizedPosts.length > 0) {
          localStorage.setItem('admin_blog_posts', JSON.stringify(normalizedPosts));
        }
      } catch (error) {
        console.error('Blog section could not load stories:', error);
        if (!active) return;
        try {
          const savedPosts = localStorage.getItem('admin_blog_posts');
          const savedCategories = localStorage.getItem('admin_blog_categories');
          if (savedPosts) {
            setPosts(JSON.parse(savedPosts).map((post: BlogPost) => normalizeBlogPost(post)));
          }
          if (savedCategories) {
            setCategories(JSON.parse(savedCategories));
          }
        } catch (fallbackError) {
          console.error('Blog section backup could not be read:', fallbackError);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadBlogData();
    window.addEventListener('blog_posts_updated', loadBlogData);
    window.addEventListener('blog_categories_updated', loadBlogData);
    return () => {
      active = false;
      window.removeEventListener('blog_posts_updated', loadBlogData);
      window.removeEventListener('blog_categories_updated', loadBlogData);
    };
  }, []);

  const storyAlt = (post: BlogPost) => photographAlt(
    post.title,
    { alt: post.title, caption: post.excerpt },
    { hero: true, description: post.excerpt }
  );

  if (variant === 'home') {
    const { lead, supporting } = homepageStories(posts, displaySettings.homepageBlogCount);
    const stories = lead ? [lead, ...supporting] : [];

    return (
      <div className={`journal journal-home ${supporting.length > 0 ? 'has-supporting' : 'is-single'} stories-${stories.length}`}>
        <header className="journal-masthead">
          <h2 className="journal-title">Blogs</h2>
        </header>

        {loading ? (
          <p className="journal-note">Loading stories…</p>
        ) : stories.length === 0 ? (
          <p className="journal-note">New stories will appear here.</p>
        ) : (
          <div className="journal-home-grid">
            {stories.map((post, index) => (
              <Link
                className={`blog-card ${index === 0 ? 'journal-lead' : 'journal-support'}`}
                key={post.id}
                to={`/blog/${post.slug}`}
              >
                {imageSource(post.heroImage) ? (
                  <img
                    className="blog-card-image"
                    {...storyPicture(imageSource(post.heroImage), stories.length, post.heroImage)}
                    alt={storyAlt(post)}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                  />
                ) : (
                  <div className="journal-photo-fallback blog-card-image" aria-hidden="true" />
                )}
                <div className="journal-copy">
                  {primaryCategory(post, categories) && (
                    <p className="journal-kicker">{primaryCategory(post, categories)}</p>
                  )}
                  <h3>{post.title}</h3>
                  {supporting.length === 0 && post.excerpt && (
                    <p className="journal-excerpt">{post.excerpt.trim()}</p>
                  )}
                  <p className="journal-date">{formatStoryDate(post.publishedAt || post.createdAt)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        <Link className="journal-all" to="/blog">All stories</Link>
      </div>
    );
  }

  const visibleCategories = categoriesInUse(posts, categories);
  const indexPosts = publishedPosts(posts).filter((post) => (
    !selectedCategory || post.categories.includes(selectedCategory)
  ));

  return (
    <div className="journal journal-index">
      <header className="journal-masthead">
        <p className="journal-kicker">Journal</p>
        <h1 className="journal-title">Stories</h1>
      </header>

      {visibleCategories.length > 0 && (
        <nav className="journal-nav" aria-label="Story categories">
          <button
            type="button"
            aria-pressed={selectedCategory === ''}
            onClick={() => setSelectedCategory('')}
          >
            All
          </button>
          {visibleCategories.map((category) => (
            <button
              key={category.id}
              type="button"
              aria-pressed={selectedCategory === category.id}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.name}
            </button>
          ))}
        </nav>
      )}

      {loading ? (
        <p className="journal-note">Loading stories…</p>
      ) : indexPosts.length === 0 ? (
        <p className="journal-note">No stories in this category yet.</p>
      ) : (
        <div className="journal-rows">
          {indexPosts.map((post) => (
            <Link className="journal-row blog-card" key={post.id} to={`/blog/${post.slug}`}>
              {imageSource(post.heroImage) ? (
                <img
                  className="blog-card-image"
                  {...storyPicture(imageSource(post.heroImage), 0, post.heroImage)}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="journal-photo-fallback blog-card-image" aria-hidden="true" />
              )}
              <div className="journal-copy">
                {primaryCategory(post, categories) && (
                  <p className="journal-kicker">{primaryCategory(post, categories)}</p>
                )}
                <h2>{post.title}</h2>
                {post.excerpt && <p className="journal-excerpt">{post.excerpt.trim()}</p>}
                <p className="journal-date">{formatStoryDate(post.publishedAt || post.createdAt)}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogSection;
